/**
 * useSetupLedger — the memo, and nothing else.
 *
 * THERE IS NO QUERY LEFT IN HERE, and that is the shape of a decision rather than an omission.
 * The ledger was going to carry one fleet-wide line — "N contracts in setup" — and it does not:
 * `get_contracts_with_status` cannot count a DRAFT that has never been updated, which is exactly
 * the in-setup population, so the number would have been wrong on the one screen whose whole
 * argument is that its numbers agree with the server's. It was also the page's only unscoped read.
 * Fleet context waits for a correct server read; until then this hook reads what the page already
 * holds and hands it to a pure function.
 *
 * THE ARGUMENTS ARE SPREAD RATHER THAN TAKEN AS ONE OBJECT so the dependency list is the real one:
 * an options object composed at the call site is a new identity on every render, and a memo keyed
 * to it recomputes every time the page draws — which on this page is every second while a seal is
 * running.
 */

import { useMemo } from 'react';

import type { AgreementTokenRow } from '@/lib/agreements/api';

import { setupLedger, type LedgerView } from './setupLedger';
import type { SetupSnapshot, SetupState } from '../setupState';

interface Options {
    state?: SetupState | null;
    snapshot?: SetupSnapshot | null;
    tokens?: AgreementTokenRow[] | null;
    contractCreatedAt?: string | null;
}

export function useSetupLedger({
    state,
    snapshot,
    tokens,
    contractCreatedAt,
}: Options): LedgerView {
    return useMemo(
        // `now` IS NOT PASSED. The selector falls to `snapshot.now`, which is the instant the page
        // built its snapshot — one clock for the manifest, the narrator and this column.
        () => setupLedger({ state, snapshot, tokens, contractCreatedAt }),
        [state, snapshot, tokens, contractCreatedAt],
    );
}
